// Banking's way in for what has no feed (gap #11): a CSV or OFX statement from the bank's site,
// through bank.importStatement (lines already in are skipped by the server: the bank's id, or date,
// amount and description), and a deposit of the cash and cheques taken in (deposits.record).

import { useState } from 'react';
import { client } from '../api.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { dayIn, dayText, money as cadText, todayIn } from './model.ts';
import { errorText, Money, Panel, useAct, useQuery } from './ui.tsx';

const GET_IT = 'Download the CSV or OFX from your bank’s website and choose that.';

/**
 * What a chosen statement file is: a CSV, an OFX, or something else said plainly, so a PDF never
 * reaches the column pickers (QA-809). Read by its content first, then its name.
 */
export function statementKind(name: string, text: string): { format: 'csv' | 'ofx' } | { wrong: string } {
  const head = text.slice(0, 2000);
  if (head.startsWith('%PDF-') || /\.pdf$/i.test(name)) return { wrong: `This is a PDF. ${GET_IT}` };
  if (head.startsWith('PK\u0003\u0004') || /\.(xlsx?|numbers|ods)$/i.test(name))
    return {
      wrong: 'This is a spreadsheet. Save it as CSV, or download the CSV or OFX from your bank’s website.',
    };
  if (/\.(ofx|qfx)$/i.test(name) || /<OFX>/i.test(head)) return { format: 'ofx' };
  // a NUL in the first lines: an image or another file that isn't text
  if (head.includes('\u0000')) return { wrong: `This isn’t a CSV or OFX file. ${GET_IT}` };
  return { format: 'csv' };
}

/** The columns a CSV's first row names, so the person can say which is which. */
export function csvHeader(text: string): string[] {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const out: string[] = [];
  let cell = '';
  let quoted = false;
  for (const ch of first) {
    if (ch === '"') quoted = !quoted;
    else if (ch === ',' && !quoted) {
      out.push(cell.trim());
      cell = '';
    } else cell += ch;
  }
  out.push(cell.trim());
  return out.filter(Boolean);
}

/** A best first guess at which column is which, from the names banks usually give them. */
export function guessColumns(cols: string[]) {
  const find = (re: RegExp) => cols.find((c) => re.test(c)) ?? '';
  return {
    date: find(/date|posted/i),
    description: find(/desc|memo|payee|details|narrative/i),
    amount: find(/^amount$|^amt$|amount \(|transaction amount/i),
    debit: find(/debit|withdrawal|money out|paid out/i),
    credit: find(/credit|deposit|money in|paid in/i),
    reference: find(/ref|cheque|check|fitid|id$/i),
  };
}

type Columns = ReturnType<typeof guessColumns>;

export function ImportStatement({
  bankAccountId,
  bankName,
  toast,
  onDone,
}: {
  bankAccountId: string;
  bankName: string;
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<{ name: string; text: string; format: 'csv' | 'ofx' } | null>(null);
  /** Why the chosen file can't be brought in (a PDF, a spreadsheet), instead of its columns. */
  const [wrong, setWrong] = useState<string | null>(null);
  const [cols, setCols] = useState<string[]>([]);
  const [map, setMap] = useState<Columns | null>(null);
  const [split, setSplit] = useState(false);
  const act = useAct(toast, async () => {
    setOpen(false);
    setFile(null);
    setWrong(null);
    await onDone();
  });
  const pick = async (f: File | undefined) => {
    if (!f) return;
    const text = await f.text();
    const kind = statementKind(f.name, text);
    if ('wrong' in kind) {
      setFile(null);
      setWrong(kind.wrong);
      return;
    }
    setWrong(null);
    const format = kind.format;
    setFile({ name: f.name, text, format });
    if (format === 'csv') {
      const c = csvHeader(text);
      const g = guessColumns(c);
      setCols(c);
      setMap(g);
      setSplit(!g.amount && !!(g.debit || g.credit));
    }
  };
  const csvOk =
    !!map && !!map.date && !!map.description && (split ? !!(map.debit || map.credit) : !!map.amount);
  const ok = !!file && (file.format === 'ofx' || csvOk);
  const run = () => {
    if (!file || !ok) return;
    void act
      .run<{ newCount: number; duplicateCount: number }>('bank.importStatement', {
        bankAccountId,
        format: file.format,
        fileName: file.name,
        content: file.text,
        ...(file.format === 'csv' && map
          ? {
              csvColumns: {
                date: map.date,
                description: map.description,
                ...(split
                  ? {
                      ...(map.debit ? { debit: map.debit } : {}),
                      ...(map.credit ? { credit: map.credit } : {}),
                    }
                  : { amount: map.amount }),
                ...(map.reference ? { reference: map.reference } : {}),
              },
            }
          : {}),
      })
      .then((r) => {
        if (r)
          toast(
            `${r.newCount} new line${r.newCount === 1 ? '' : 's'} from ${file.name}` +
              (r.duplicateCount ? `; ${r.duplicateCount} already in, skipped.` : '.'),
          );
      });
  };
  const select = (key: keyof Columns, label: string, optional = false) => (
    <label>
      <span className="flabel">{label}</span>
      <select
        className="field"
        value={map?.[key] ?? ''}
        onChange={(e) => setMap({ ...(map as Columns), [key]: e.target.value })}
      >
        <option value="">{optional ? 'None' : 'Pick a column…'}</option>
        {cols.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <Panel title="Bring in a statement">
      {!open ? (
        <>
          <p className="muted small">
            A CSV or OFX file from {bankName}’s website. Lines already in are skipped.
          </p>
          <div className="row-actions">
            <button className="btn" onClick={() => setOpen(true)}>
              Choose a file…
            </button>
          </div>
        </>
      ) : (
        <form
          className="fgrid"
          aria-label="Bring in a statement"
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <label className="wide">
            <span className="flabel">Statement file (CSV or OFX)</span>
            <input
              className="field"
              type="file"
              accept=".csv,.ofx,.qfx,text/csv"
              onChange={(e) => void pick(e.target.files?.[0])}
            />
          </label>
          {wrong && (
            <p className="wide bad-text small" role="alert">
              {wrong}
            </p>
          )}
          {file?.format === 'ofx' && <p className="wide muted small">An OFX file names its own columns.</p>}
          {file?.format === 'csv' && map && (
            <>
              {select('date', 'Date')}
              {select('description', 'Description')}
              <label className="checkrow wide">
                <input type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} /> Money
                in and out are in two columns
              </label>
              {split ? (
                <>
                  {select('debit', 'Money out')}
                  {select('credit', 'Money in')}
                </>
              ) : (
                select('amount', 'Amount')
              )}
              {select('reference', 'Reference', true)}
            </>
          )}
          <div className="row-actions">
            <button className="btn primary" type="submit" disabled={!ok || act.busy}>
              {act.busy ? 'Bringing it in…' : 'Bring it in'}
            </button>
            <button
              className="btn quiet"
              type="button"
              onClick={() => {
                setOpen(false);
                setFile(null);
                setWrong(null);
              }}
              disabled={act.busy}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

// ── a deposit of cash and cheques (gap #11) ──

interface Waiting {
  id: string;
  customerName: string;
  method: 'cash' | 'cheque' | 'card' | 'other';
  amountCents: number;
  receivedAt: string;
  reference: string | null;
}

const METHOD_WORD = { cash: 'Cash', cheque: 'Cheque', card: 'Card', other: 'Other' } as const;

/**
 * Cash and cheques taken in sit in undeposited funds until they reach the bank: tick the ones on
 * the deposit slip, say when and where they went in, and deposits.record moves them, behind the
 * money confirm sheet.
 */
export function RecordDeposit({
  bankAccountId,
  bankName,
  timeZone,
  toast,
  onDone,
}: {
  bankAccountId: string;
  bankName: string;
  timeZone?: string;
  toast: (m: string) => void;
  /** Deposited: how much went in, for the page to read its book balance until the books have it. */
  onDone: (depositedCents: number) => Promise<void>;
}) {
  const waiting = useQuery<{ payments: Waiting[]; totalCents: number }>('payments.undeposited', {
    kind: 'cash_cheque',
  });
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [depositedOn, setDepositedOn] = useState(todayIn(timeZone));
  const [reference, setReference] = useState('');
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The deposit's id is held while the same slip is on screen, so a retry deposits it once.
  const save = useSaveKey(JSON.stringify([bankAccountId, depositedOn, [...skipped].sort()]));
  if (!waiting.data || waiting.data.payments.length === 0) return null;
  const picked = waiting.data.payments.filter((p) => !skipped.has(p.id));
  const total = picked.reduce((t, p) => t + p.amountCents, 0);
  const flip = (id: string) =>
    setSkipped((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const record = () => {
    setBusy(true);
    setError(null);
    client
      .call('deposits.record', {
        depositId: save.key,
        bankAccountId,
        kind: 'cash_cheque',
        depositedOn,
        paymentIds: picked.map((p) => p.id),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      })
      .then(async () => {
        setSheet(false);
        setSkipped(new Set());
        setReference('');
        save.renew();
        toast(`Deposited ${cadText(total)} to ${bankName}.`);
        await Promise.all([waiting.reload(), onDone(total)]);
      })
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };
  return (
    <Panel
      title="Cash and cheques to deposit"
      aside={<Money cents={waiting.data.totalCents} className="amount" />}
    >
      <form
        className="fgrid"
        aria-label="A deposit"
        onSubmit={(e) => {
          e.preventDefault();
          if (picked.length) setSheet(true);
        }}
      >
        <fieldset className="full deposit-slip">
          <legend className="flabel">On the deposit slip</legend>
          {waiting.data.payments.map((p) => (
            <label key={p.id} className="checkrow">
              <input type="checkbox" checked={!skipped.has(p.id)} onChange={() => flip(p.id)} />
              <span className="slip-what">
                {p.customerName}
                <span className="muted small block">
                  {METHOD_WORD[p.method]}
                  {p.reference ? ` ${p.reference}` : ''} · taken {dayText(dayIn(p.receivedAt, timeZone))}
                </span>
              </span>
              <Money cents={p.amountCents} className="amount" />
            </label>
          ))}
        </fieldset>
        <label>
          <span className="flabel">Deposited on</span>
          <input
            className="field"
            type="date"
            value={depositedOn}
            onChange={(e) => setDepositedOn(e.target.value)}
          />
        </label>
        <label>
          <span className="flabel">Deposit slip number</span>
          <input
            className="field"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <div className="row-actions">
          <button className="btn primary" type="submit" disabled={!picked.length || !depositedOn}>
            Deposit {cadText(total)}
          </button>
        </div>
      </form>
      {sheet && (
        <MoneySheet
          title="Record the deposit"
          outcome={
            <>
              {picked.length} payment{picked.length === 1 ? '' : 's'}, {cadText(total)} in all, went into{' '}
              {bankName} on {dayText(depositedOn)}. They leave undeposited funds, and the bank line can be
              matched to this deposit.
            </>
          }
          confirm={`Record ${cadText(total)}`}
          ready={!!picked.length}
          busy={busy}
          error={error}
          onConfirm={record}
          onClose={() => setSheet(false)}
        />
      )}
    </Panel>
  );
}
